/**
 * The chat composer: attach button (file picker) + auto-growing textarea +
 * send. Enter sends, Shift+Enter newlines. Send is disabled while streaming,
 * while any attachment is still uploading, or when there is nothing to send.
 * Staged files render in the AttachmentPreviews strip above the input.
 */
import {
  PaperclipIcon,
  PaperPlaneRightIcon,
  StopIcon,
} from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { useAttachments } from '@/features/chat/data-access/useAttachments';
import { useChat } from '@/features/chat/data-access/useChat';
import { Button, Textarea } from '@andavo/web-ui';
import { AttachmentPreviews } from './AttachmentPreviews';

export function Composer() {
  const { send, cancel, isStreaming } = useChat();
  const { pendingAttachments, addFiles, removeAttachment, uploading } =
    useAttachments();
  const [value, setValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasReadyAttachment = pendingAttachments.some((a) => !a.uploading);
  const canSend =
    !isStreaming &&
    !uploading &&
    (value.trim().length > 0 || hasReadyAttachment);

  const submit = () => {
    if (!canSend) return;
    void send(value);
    setValue('');
  };

  return (
    <div className="border-border-subtle border-t bg-surface-background p-4">
      <AttachmentPreviews
        attachments={pendingAttachments}
        onRemove={removeAttachment}
      />
      <div className="mx-auto flex max-w-3xl items-end gap-2">
        <input
          accept="image/*,.pdf,.txt,.csv,.xls,.xlsx"
          className="hidden"
          // data-testid="chat-attach-input" — the hidden file input the e2e
          // attachments spec drives via setInputFiles (selectors.ts attachInput).
          data-testid="chat-attach-input"
          multiple
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            if (files.length > 0) void addFiles(files);
            // Reset so selecting the same file again re-triggers change.
            e.target.value = '';
          }}
          ref={fileInputRef}
          type="file"
        />
        <Button
          aria-label="Attach file"
          // data-testid="chat-attach-button" — e2e attachments spec attach
          // affordance (selectors.ts attachButton).
          data-testid="chat-attach-button"
          disabled={isStreaming}
          onClick={() => fileInputRef.current?.click()}
          size="icon"
          variant="outline"
        >
          <PaperclipIcon className="size-icon-sm" />
        </Button>
        <Textarea
          className="max-h-40 min-h-[44px] flex-1 resize-none"
          // data-testid="chat-composer-input" — the composer textarea the e2e
          // chat spec types into (selectors.ts chatComposer). Nomad Textarea
          // spreads ...props onto the <textarea>, so this forwards cleanly.
          data-testid="chat-composer-input"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Ask about fare rules, exchanges, Sabre commands…"
          value={value}
        />
        {isStreaming ? (
          // While streaming, the send affordance becomes a Stop button that
          // aborts the in-flight reply (Composer is where the user's eyes are).
          // Esc also triggers this (see useKeyboardShortcuts).
          <Button
            aria-label="Stop generating"
            // data-testid="chat-stop-button" — the abort action the e2e chat
            // spec clicks mid-stream (selectors.ts chatStopButton).
            data-testid="chat-stop-button"
            onClick={cancel}
            size="icon"
            variant="outline"
          >
            <StopIcon className="size-icon-sm" weight="fill" />
          </Button>
        ) : (
          <Button
            aria-label="Send"
            // data-testid="chat-send-button" — the send action the e2e chat spec
            // clicks (selectors.ts chatSendButton).
            data-testid="chat-send-button"
            disabled={!canSend}
            onClick={submit}
            size="icon"
          >
            <PaperPlaneRightIcon className="size-icon-sm" />
          </Button>
        )}
      </div>
    </div>
  );
}
